import { useTooltipPortal } from './useTooltipPortal';
import { zIndexValue as zIndexPosition } from '../helpers/position';
import { useClick, useCombineCallbacks, useDeviceType, useMouseHover } from '../hooks';

/*
 Helper which returns parent ref and portal node to render, events are already binded to ref
 */
export var useTooltip = function useTooltip(_ref) {
  var open = _ref.open,
      _ref$defaultOpen = _ref.defaultOpen,
      defaultOpen = _ref$defaultOpen === void 0 ? false : _ref$defaultOpen,
      _ref$withOverflowHidd = _ref.withOverflowHidden,
      withOverflowHidden = _ref$withOverflowHidd === void 0 ? false : _ref$withOverflowHidd,
      _ref$animate = _ref.animate,
      animate = _ref$animate === void 0 ? true : _ref$animate,
      parentQuerySelector = _ref.parentQuerySelector,
      parentElement = _ref.parentElement,
      _ref$offset = _ref.offset,
      offset = _ref$offset === void 0 ? 8 : _ref$offset,
      _ref$multiline = _ref.multiline,
      multiline = _ref$multiline === void 0 ? false : _ref$multiline,
      _ref$trigger = _ref.trigger,
      triggerProp = _ref$trigger === void 0 ? 'hover' : _ref$trigger,
      _ref$position = _ref.position,
      position = _ref$position === void 0 ? 'top' : _ref$position,
      onClick = _ref.onClick,
      onOpen = _ref.onOpen,
      onClose = _ref.onClose,
      onToggle = _ref.onToggle,
      onOpened = _ref.onOpened,
      onClosed = _ref.onClosed,
      onToggled = _ref.onToggled,
      _ref$closeOnScroll = _ref.closeOnScroll,
      closeOnScroll = _ref$closeOnScroll === void 0 ? false : _ref$closeOnScroll,
      _ref$closeOnResize = _ref.closeOnResize,
      closeOnResize = _ref$closeOnResize === void 0 ? false : _ref$closeOnResize,
      _ref$closeOnOutsideCl = _ref.closeOnOutsideClick,
      closeOnOutsideClick = _ref$closeOnOutsideCl === void 0 ? true : _ref$closeOnOutsideCl,
      content = _ref.content,
      renderContent = _ref.renderContent,
      _ref$zIndex = _ref.zIndex,
      zIndex = _ref$zIndex === void 0 ? zIndexPosition('tooltip') : _ref$zIndex,
      _ref$noPadding = _ref.noPadding,
      noPadding = _ref$noPadding === void 0 ? false : _ref$noPadding,
      _ref$withMargin = _ref.withMargin,
      withMargin = _ref$withMargin === void 0 ? false : _ref$withMargin,
      wrapperStyle = _ref.wrapperStyle,
      customWrapper = _ref.customWrapper,
      width = _ref.width,
      height = _ref.height,
      disabled = _ref.disabled,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'small' : _ref$size,
      _ref$appearType = _ref.appearType,
      appearType = _ref$appearType === void 0 ? 'slide' : _ref$appearType,
      _ref$inverse = _ref.inverse,
      inverse = _ref$inverse === void 0 ? false : _ref$inverse,
      _ref$blurred = _ref.blurred,
      blurred = _ref$blurred === void 0 ? false : _ref$blurred,
      _ref$noFlip = _ref.noFlip,
      noFlip = _ref$noFlip === void 0 ? true : _ref$noFlip,
      excludeRef = _ref.excludeRef,
      closeDelay = _ref.closeDelay,
      component = _ref.component;

  var _useDeviceType = useDeviceType(),
      hasTouchScreen = _useDeviceType.hasTouchScreen;

  var trigger = triggerProp === null ? null : hasTouchScreen ? 'click' : triggerProp;

  var _useTooltipPortal = useTooltipPortal({
    open: open,
    defaultOpen: defaultOpen,
    withOverflowHidden: withOverflowHidden,
    animate: animate,
    parentQuerySelector: parentQuerySelector,
    parentElement: parentElement,
    offset: offset,
    multiline: multiline,
    trigger: trigger,
    position: position,
    onClick: onClick,
    onOpen: onOpen,
    onClose: onClose,
    onToggle: onToggle,
    onOpened: onOpened,
    onClosed: onClosed,
    onToggled: onToggled,
    content: content,
    renderContent: renderContent,
    zIndex: zIndex,
    noPadding: noPadding,
    withMargin: withMargin,
    wrapperStyle: wrapperStyle,
    customWrapper: customWrapper,
    width: width,
    height: height,
    size: size,
    appearType: appearType,
    component: component,
    inverse: inverse,
    blurred: blurred,
    noFlip: noFlip,
    closeOnScroll: closeOnScroll,
    closeOnResize: closeOnResize,
    closeOnOutsideClick: closeOnOutsideClick,
    disabled: disabled,
    excludeRef: excludeRef,
    closeDelay: closeDelay
  }),
      ref = _useTooltipPortal.ref,
      childRef = _useTooltipPortal.childRef,
      onMouseClick = _useTooltipPortal.onMouseClick,
      onMouseOver = _useTooltipPortal.onMouseOver,
      onMouseOut = _useTooltipPortal.onMouseOut,
      onChildMouseOver = _useTooltipPortal.onChildMouseOver,
      onChildMouseOut = _useTooltipPortal.onChildMouseOut,
      portalNode = _useTooltipPortal.portalNode;

  var _useClick = useClick(onMouseClick, ref, disabled || trigger !== 'click'),
      setClickRef = _useClick.setRef;

  var _useMouseHover = useMouseHover(onMouseOver, onMouseOut, ref, disabled || trigger !== 'hover' && trigger !== 'enter'),
      setHoverRef = _useMouseHover.setRef;

  var setRef = useCombineCallbacks(setClickRef, setHoverRef);

  var _useMouseHover2 = useMouseHover(onChildMouseOver, onChildMouseOut, null, disabled || trigger !== 'enter'),
      setExcludedHoverRef = _useMouseHover2.setRef;

  setExcludedHoverRef(childRef.current);
  return {
    ref: ref,
    setRef: setRef,
    portalNode: portalNode,
    trigger: trigger
  };
};